// Toolbar with search, sort dropdown, and filter dropdown
// Controls search/filter/sort state via callbacks

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationStatus } from '@startus/applications';

// Sort option definition
export interface SortOption {
  label: string;
  sortBy: 'endDate' | 'createdAt';
  sortOrder: 'asc' | 'desc';
}

interface ApplicationsToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  sortOptions: SortOption[];
  activeSortIndex: number;
  onSortChange: (index: number) => void;
  statusFilter: ApplicationStatus | undefined;
  onStatusFilterToggle: (status: ApplicationStatus) => void;
  statusLabels: Record<ApplicationStatus, string>;
  searchPlaceholder: string;
  sortLabel: string;
  filterLabel: string;
}

export function ApplicationsToolbar({
  searchValue,
  onSearchChange,
  sortOptions,
  activeSortIndex,
  onSortChange,
  statusFilter,
  onStatusFilterToggle,
  statusLabels,
  searchPlaceholder,
  sortLabel,
  filterLabel,
}: ApplicationsToolbarProps) {
  const { t } = useTranslation('common');
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setShowSort(false);
      }
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="flex items-center">
      {/* Search input */}
      <div className="flex-1 relative">
        <svg
          className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#F9F9F9] z-10 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="bg-[#2E2E2E] text-white placeholder-[#CACACA] input w-full pl-10 rounded-full border-0 outline-none focus:outline-none ring-0 focus:ring-0"
        />
      </div>

      {/* Sort dropdown */}
      <div ref={sortRef} className="relative">
        <button
          type="button"
          onClick={() => {
            setShowSort(!showSort);
            setShowFilter(false);
          }}
          className="btn btn-ghost btn-square"
          aria-label={sortLabel}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M17 8L12 3L7 8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M17 16L12 21L7 16"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showSort && (
          <div className="absolute right-0 top-full mt-1 bg-[#1E1E1E] border border-base-300 rounded-2xl shadow-xl z-20 min-w-65 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm text-white">{sortLabel}</span>
              <button
                type="button"
                onClick={() => setShowSort(false)}
                className="text-white/60 hover:text-white transition-colors"
                aria-label={t('common.close')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Sort options */}
            <div className="flex flex-col gap-1">
              {sortOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    idx === activeSortIndex
                      ? 'bg-[#19A2F8]/40 text-white'
                      : 'bg-[#2E2E2E] text-white hover:bg-[#3a3a3a]'
                  }`}
                  onClick={() => {
                    onSortChange(idx);
                    setShowSort(false);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filter dropdown */}
      <div ref={filterRef} className="relative">
        <button
          type="button"
          onClick={() => {
            setShowFilter(!showFilter);
            setShowSort(false);
          }}
          className={`btn btn-ghost btn-square ${statusFilter ? 'text-primary' : ''}`}
          aria-label={filterLabel}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M3.99961 3H19.9997C20.552 3 20.9997 3.44764 20.9997 3.99987L20.9999 5.58569C21 5.85097 20.8946 6.10538 20.707 6.29295L14.2925 12.7071C14.105 12.8946 13.9996 13.149 13.9996 13.4142L13.9996 19.7192C13.9996 20.3698 13.3882 20.8472 12.7571 20.6894L10.7571 20.1894C10.3119 20.0781 9.99961 19.6781 9.99961 19.2192L9.99961 13.4142C9.99961 13.149 9.89425 12.8946 9.70672 12.7071L3.2925 6.29289C3.10496 6.10536 2.99961 5.851 2.99961 5.58579V4C2.99961 3.44772 3.44732 3 3.99961 3Z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showFilter && (
          <div className="absolute right-0 top-full mt-1 bg-[#1E1E1E] border border-base-300 rounded-2xl shadow-xl z-20 min-w-65 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm text-white">{filterLabel}</span>
              <button
                type="button"
                onClick={() => setShowFilter(false)}
                className="text-white/60 hover:text-white transition-colors"
                aria-label={t('common.close')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Filter options */}
            <div className="flex flex-col gap-1">
              {Object.values(ApplicationStatus).map((status) => (
                <label
                  key={status}
                  className="flex items-center gap-3 px-3 py-2.5 bg-[#2E2E2E] rounded-lg cursor-pointer hover:bg-[#3a3a3a] transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={statusFilter === status}
                    onChange={() => onStatusFilterToggle(status)}
                    className="checkbox checkbox-sm rounded border-2 border-[#CACACA] checked:bg-[#19A2F8] checked:border-[#19A2F8]"
                  />
                  <span className="text-sm text-white">{statusLabels[status]}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
